import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { DealsGroupFindDto, type DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import { HelpdeskTaskFindDto, type HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import { ProjectFindDto, type ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { BudgetRequestFindDto, type BudgetRequestFindInterface } from '@zaoblako/shared/modules/task/dtos/budget-request/budget-request-find.dto';
import { TaskFindDto, type TaskFindInterface } from '@zaoblako/shared/modules/task/dtos/task/task-find.dto';
import { ThreadTypeEnum } from '@zaoblako/shared/modules/messenger/enums/thread-type.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { PlannedIncomeFindDto, type PlannedIncomeFindInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-find.dto';
import { TransitTaskFindDto, type TransitTaskFindInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-find.dto';
import { DebtCollectionTaskFindDto, type DebtCollectionTaskFindInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-find.dto';
import { ChatFindDto, type ChatFindInterface } from '@zaoblako/shared/modules/ai/dtos/chat/chat-find.dto';

import * as yup from 'yup';

/** Thread response body - DTO signature */
export interface ThreadFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech columns
  /** Thread creation date and time */
  created: Date;
  /** Thread last update date and time */
  updated: Date;
  /** Thread soft deletion date and time */
  deleted: Date;
  /** Thread creator */
  createdBy?: UserFindInterface;

  // Data columns
  /** Thread display name */
  name: string;
  /** Thread type */
  type: ThreadTypeEnum;
  /** Thread relation entity unique identifier */
  relatedEntityId?: number;

  // Unique thread relations
  /** Project this thread belongs to */
  project?: ProjectFindInterface;
  /** BRS task this thread belongs to */
  brsTask?: TaskFindInterface;
  /** Budget request this thread belongs to */
  budgetRequest?: BudgetRequestFindInterface;
  /** Deal this thread belongs to */
  deal?: DealFindInterface;
  /** Deals' group this thread belongs to */
  dealsGroup?: DealsGroupFindInterface;
  /** HD task this thread belongs to */
  helpdeskTask?: HelpdeskTaskFindInterface;
  /** Transit task this thread belongs to */
  transitTask?: TransitTaskFindInterface;
  /** Planned income this thread belongs to */
  plannedIncome?: PlannedIncomeFindInterface;
  /** Debt collection task this thread belongs to */
  debtCollectionTask?: DebtCollectionTaskFindInterface;
  /** Frm AI chat this thread belongs to */
  frmAiChat?: ChatFindInterface;
}

/** Thread response body - DTO schema */
export const ThreadFindDto = SerdeDtoClass.create<ThreadFindInterface>(MessengerDtoKeysEnum.THREAD_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Удален',
    serializer: yup.date(),
  },
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  type: {
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(ThreadTypeEnum)),
  },
  relatedEntityId: {
    optional: true,
    label: 'Номер объекта',
    serializer: yup.number(),
  },
  project: {
    optional: true,
    label: 'Задача',
    nested: ProjectFindDto,
    serializer: (_, nested) => nested,
  },
  brsTask: {
    optional: true,
    label: 'Заявка на платеж',
    nested: TaskFindDto,
    serializer: (_, nested) => nested,
  },
  budgetRequest: {
    optional: true,
    label: 'Заявка на бюджет',
    nested: BudgetRequestFindDto,
    serializer: (_, nested) => nested,
  },
  deal: {
    optional: true,
    label: 'Сделка',
    nested: BusinessDtoKeysEnum.DEAL_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  dealsGroup: {
    optional: true,
    label: 'Группа сделок',
    nested: DealsGroupFindDto,
    serializer: (_, nested) => nested,
  },
  helpdeskTask: {
    optional: true,
    label: 'Заявка HelpDesk',
    nested: HelpdeskTaskFindDto,
    serializer: (_, nested) => nested,
  },
  transitTask: {
    optional: true,
    label: 'Заявка на проезд',
    nested: TransitTaskFindDto,
    serializer: (_, nested) => nested,
  },
  plannedIncome: {
    optional: true,
    label: 'Планируемое поступление',
    nested: PlannedIncomeFindDto,
    serializer: (_, nested) => nested,
  },
  debtCollectionTask: {
    optional: true,
    label: 'Заявка на взыскание задолженности',
    nested: DebtCollectionTaskFindDto,
    serializer: (_, nested) => nested,
  },
  frmAiChat: {
    optional: true,
    label: 'FRM AI chat',
    nested: ChatFindDto,
    serializer: (_, nested) => nested,
  },
});
